import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Radio,
  RadioGroup,
  Typography,
  Box,
  TextField,
  InputAdornment,
  Skeleton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { BIBlue, BILightGrey } from "../../assets/helpers";
import { Language } from "@mui/icons-material";
import { Postrequestcall } from "../../request/Postrequest";
import { FETCH_TRANSLATOR_LANGUAGES, SET_DEFULT_CHAT_GROUP_LANG } from "../../constant/Apipath";
import { useDispatch, useSelector } from "react-redux";
import { setLogindata } from "../../redux/actions/actions";
import { Getrequestcall } from "../../request/Getrequest";
import { db } from "../../db";

const LanguageModal = (props) => {
  const { open, onClose, languageData, setLanguageData, getData } = props;
  const [view, setView] = useState("settings"); // "settings" | "languages"
  const [searchTerm, setSearchTerm] = useState("");
  const [languageList, setLanguageList] = useState([]);
  const [isListLoading, setIsListLoading] = useState(false);
  const { loginData } = useSelector((state) => state?.main);
  const dispatch = useDispatch();

  const handleLanguageClick = () => setView("languages");
  const handleBack = () => setView("settings");

  const handleLangChange = async (langObj) => {
    if (langObj) {
      setLanguageData({
        languageName: langObj.language,
        languageCode: langObj.code
      });
      try {
        let getResponse = await Getrequestcall(
          `${SET_DEFULT_CHAT_GROUP_LANG}/${langObj.code}`,
          loginData?.accessToken
        );
        if (getResponse?.status === 200) {
          dispatch(
            setLogindata({
              ...loginData,
              ChatLanguage: langObj.code
            })
          );
          await db.messages.clear();
          await db.chats.clear();
        }
      } catch (error) {
        console.error("handleLangChange-> Error setting default chat language: ", error);
      }
      setView("settings");
      onClose();
      await getData();
    }
  };

  const filteredLanguages = languageList.filter((lang) =>
    lang.language.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getLanguageList = async () => {
    setIsListLoading(true);
    try {
      const response = await Postrequestcall(
        FETCH_TRANSLATOR_LANGUAGES,
        { PageNr: 1, NrOfRecPerPage: 200 },
        loginData?.accessToken
      );
      if (response?.data?.data) {
        const langs = response.data.data;
        setLanguageList(langs);
        // set default language if available
        const defaultLang = langs.find((lang) => lang.code === loginData?.ChatLanguage);
        if (defaultLang) {
          setLanguageData({
            languageName: defaultLang.language,
            languageCode: defaultLang.code
          });
        }
        setIsListLoading(false);
      }
    } catch (error) {
      console.error("getLanguageList-> Error fetching language list: ", error);
      setIsListLoading(false);
    }
  };

  useEffect(() => {
    getLanguageList();
  }, []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: "12px", p: 0 }
      }}>
      {/* Header */}
      <DialogTitle
        sx={{
          fontSize: "16px",
          fontWeight: 600,
          p: 2,
          borderBottom: "1px solid #eee",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}>
        {view === "settings" ? "Chat Settings" : "Select Language"}
        <IconButton size="small" onClick={view === "settings" ? onClose : handleBack}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {view === "settings" ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary" }}>
              Translation
            </Typography>
            <Box
              onClick={handleLanguageClick}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 1.5,
                border: "1px solid #ddd",
                borderRadius: "8px",
                cursor: "pointer",
                "&:hover": { backgroundColor: "#f9f9f9" }
              }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Language sx={{ color: BIBlue, fontSize: 22 }} />
                <Typography variant="body2">Select Language</Typography>
              </Box>
              <Typography variant="body2" color="primary" fontWeight={500}>
                {languageData?.languageName?.toUpperCase() || "ENGLISH"}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box sx={{ maxHeight: 500, overflowY: "auto" }}>
            {/* Sticky Search Bar */}
            <Box
              sx={{
                position: "sticky",
                top: 0,
                backgroundColor: "#fff",
                zIndex: 1,
                p: 1.5,
                borderBottom: "1px solid #eee"
              }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search languages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "14px"
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  )
                }}
              />
            </Box>

            {/* Language List */}
            <RadioGroup value={languageData?.languageName} sx={{ width: "100%" }}>
              <List disablePadding>
                {isListLoading ? (
                  // Show skeleton loaders when loading
                  Array.from(new Array(9)).map((_, index) => (
                    <ListItem key={index} sx={{ py: 0.5, px: 2 }}>
                      <Skeleton variant="text" width="60%" height={24} />
                      <Skeleton variant="circular" width={20} height={20} sx={{ ml: "auto" }} />
                    </ListItem>
                  ))
                ) : filteredLanguages?.length > 0 ? (
                  filteredLanguages?.map((langObj) => (
                    <ListItem
                      key={langObj?.id}
                      sx={{
                        py: 0.5,
                        px: 2,
                        cursor: "pointer",
                        "&:hover": { backgroundColor: BILightGrey }
                      }}
                      button
                      onClick={() => handleLangChange(langObj)}>
                      <ListItemText primary={langObj?.language} />
                      <Radio
                        value={langObj?.language}
                        checked={languageData?.languageName === langObj?.language}
                      />
                    </ListItem>
                  ))
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ textAlign: "center", py: 2, color: "text.secondary" }}>
                    No languages found
                  </Typography>
                )}
              </List>
            </RadioGroup>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LanguageModal;
